import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, TextInput } from 'grommet';
import { DataTableHeader, DataTableItem, LoadingSpinner, Text } from '/src/components';
import { ReferralCommission } from '/src/lib/models/ReferralCommission';
import { getPeriodFromDateString } from '/src/utils';

interface EditableReferralCommission extends ReferralCommission {
  isEditing?: boolean;
  editedPercentRate?: string;
}

export type ReferralCommissionsListProps = {
  referralCommissions?: ReferralCommission[];
  isYearly?: boolean;
};

export const ReferralCommissionsList: React.FC<ReferralCommissionsListProps> = ({
  referralCommissions: initialReferralCommissions,
  isYearly = false,
}) => {
  const { t } = useTranslation();
  const [referralCommissions, setReferralCommissions] = useState<EditableReferralCommission[]>(
    initialReferralCommissions || []
  );

  useEffect(() => {
    if (initialReferralCommissions) {
      setReferralCommissions(initialReferralCommissions);
    }
  }, [initialReferralCommissions]);

  return !referralCommissions ? (
    <LoadingSpinner />
  ) : (
    <DataTable
      pad={{ horizontal: '1.5rem' }}
      columns={[
        {
          property: 'referral_partner',
          primary: true,
          size: '35%',
          header: <DataTableHeader>{t('commissions:referralPartner')}</DataTableHeader>,
          render: (referralCommission) => <Text>{referralCommission.referral_partner?.name || '—'}</Text>,
        },
        {
          property: 'percent_rate',
          size: '32%',
          header: <DataTableHeader>{t('commissions:percentage')}</DataTableHeader>,
          render: (referralCommission) => (
            <DataTableItem boxProps={{ pad: { vertical: '1rem' } }}>
              {referralCommission.isEditing ? (
                <TextInput
                  type="number"
                  value={referralCommission.editedPercentRate ?? referralCommission.percent_rate.toString()}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setReferralCommissions((prev) =>
                        prev.map((c) => (c.id === referralCommission.id ? { ...c, editedPercentRate: value } : c))
                      );
                    }
                  }}
                  style={{ width: '80px' }}
                />
              ) : (
                `${referralCommission.percent_rate}%`
              )}
            </DataTableItem>
          ),
        },
        {
          property: 'start_reporting_quarter',
          size: '16%',
          header: <DataTableHeader>{t('commissions:effectiveStartDate')}</DataTableHeader>,
          render: (referralCommission) => (
            <DataTableItem>
              {getPeriodFromDateString(referralCommission.start_reporting_quarter, isYearly)}
            </DataTableItem>
          ),
        },
        {
          property: 'end_reporting_quarter',
          size: '16%',
          header: <DataTableHeader>{t('commissions:effectiveEndDate')}</DataTableHeader>,
          render: (referralCommission) => (
            <DataTableItem>
              {referralCommission.end_reporting_quarter === '2999-10-01' ? (
                '—'
              ) : (
                <Text>{getPeriodFromDateString(referralCommission.end_reporting_quarter as string, isYearly)}</Text>
              )}
            </DataTableItem>
          ),
        },
      ]}
      data={referralCommissions}
      background={['light-6', 'white']}
    />
  );
};
